/**
 * Platform Status Service - real health data from the /api/status endpoint.
 *
 * Sources:
 *   • /api/status     - Redis health, AI provider availability, server uptime
 *   • Supabase client - DB reachability probe + incidents realtime subscription
 *   • /api/incidents  - canonical incident persistence (service-role writes)
 *
 * Incident persistence:
 *   • reportIncident() writes to Supabase via /api/incidents (fire-and-forget)
 *   • resolveIncident() writes to Supabase via PATCH /api/incidents/:id/resolve
 *   • initIncidentRealtime() subscribes to postgres_changes on the incidents
 *     table so all tabs/sessions stay in sync without polling
 *   • fetchPlatformStatus() hydrates activeIncidents from Supabase on every call
 *
 * The in-memory store is a cache, never the authoritative source for incidents.
 */

import { supabase } from "@/integrations/supabase/client";
import { authHeader } from "@/api/authToken";
import type { RealtimeChannel } from "@supabase/supabase-js";

export type ServiceStatus = "operational" | "degraded" | "outage";

export interface ServiceHealth {
  name:        string;
  status:      ServiceStatus;
  description: string;
  lastChecked: string;
}

export interface PlatformIncident {
  id:          string;
  title:       string;
  description: string;
  severity:    "minor" | "major" | "critical";
  status:      "investigating" | "identified" | "monitoring" | "resolved";
  services:    string[];
  createdAt:   string;
  updatedAt:   string;
  resolvedAt?: string;
}

export interface PlatformStatusData {
  overall:         ServiceStatus;
  message:         string | null;
  services:        ServiceHealth[];
  activeIncidents: PlatformIncident[];
  recentIncidents: PlatformIncident[];
}

/* =========================================================
   UUID guard - distinguishes Supabase UUIDs from temp IDs
   ========================================================= */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/* =========================================================
   Internal store
   ========================================================= */

let currentStatus: PlatformStatusData = {
  overall:         "operational",
  message:         null,
  services:        [],
  activeIncidents: [],
  recentIncidents: [],
};

const listeners = new Set<(s: PlatformStatusData) => void>();

/* =========================================================
   Supabase row → PlatformIncident
   ========================================================= */

function rowToIncident(row: Record<string, unknown>): PlatformIncident {
  return {
    id:          row.id as string,
    title:       row.title as string,
    description: (row.description as string) ?? "",
    severity:    (row.severity as PlatformIncident["severity"]) ?? "minor",
    status:      row.status === "resolved" ? "resolved" : "investigating",
    services:    (row.affected_services as string[]) ?? [],
    createdAt:   row.created_at as string,
    updatedAt:   (row.resolved_at as string) ?? (row.created_at as string),
    resolvedAt:  (row.resolved_at as string | undefined) ?? undefined,
  };
}

/* =========================================================
   Supabase incident reads
   ========================================================= */

async function loadIncidentsFromSupabase(): Promise<{
  active: PlatformIncident[];
  recent: PlatformIncident[];
}> {
  const [activeRes, recentRes] = await Promise.allSettled([
    supabase
      .from("incidents")
      .select("*")
      .eq("status", "active")
      .order("created_at", { ascending: false }),
    supabase
      .from("incidents")
      .select("*")
      .eq("status", "resolved")
      .order("resolved_at", { ascending: false })
      .limit(20),
  ]);

  return {
    active: (activeRes.status === "fulfilled" ? (activeRes.value.data ?? []) : [])
      .map(rowToIncident),
    recent: (recentRes.status === "fulfilled" ? (recentRes.value.data ?? []) : [])
      .map(rowToIncident),
  };
}

async function refreshIncidentsFromSupabase(): Promise<void> {
  try {
    const { active, recent } = await loadIncidentsFromSupabase();
    currentStatus = { ...currentStatus, activeIncidents: active, recentIncidents: recent };
    recalculateOverall();
    listeners.forEach((fn) => fn(currentStatus));
  } catch (err) {
    console.warn("[platformStatus] incident refresh failed:", err instanceof Error ? err.message : err);
  }
}

/* =========================================================
   Auth helper - JWT for API write calls
   ========================================================= */

async function getBearerHeader(): Promise<Record<string, string>> {
  // Hardened shared read: retries auth-lock contention and falls back to the
  // persisted session, so incident writes never silently drop while signed in.
  return authHeader();
}

/* =========================================================
   Realtime channel - singleton
   Subscribed once from Layout via initIncidentRealtime().
   ========================================================= */

let incidentChannel: RealtimeChannel | null = null;

export function initIncidentRealtime(): () => void {
  incidentChannel?.unsubscribe();

  incidentChannel = supabase
    .channel("nuvra-incidents-global")
    .on(
      "postgres_changes",
      { event: "*", schema: "public", table: "incidents" },
      () => { refreshIncidentsFromSupabase(); },
    )
    .subscribe();

  refreshIncidentsFromSupabase();

  return () => {
    incidentChannel?.unsubscribe();
    incidentChannel = null;
  };
}

/* =========================================================
   Real fetch from /api/status + Supabase probe + incidents
   ========================================================= */

interface ApiStatusResponse {
  services: { api: string; redis: string; ai: string };
  ai:       { providers: string[]; activeCount: number };
  uptime:   number;
  redis:    { state: string };
  timestamp: string;
}

function toServiceStatus(raw: string): ServiceStatus {
  if (raw === "degraded") return "degraded";
  if (raw === "outage")   return "outage";
  return "operational";
}

/** Human uptime that never reads as a broken "0h" for a freshly started server. */
function formatUptime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "Uptime unavailable";
  if (seconds < 60)    return "Uptime under a minute";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60)    return `Uptime ${minutes}m`;
  const hours = Math.floor(seconds / 3600);
  if (hours < 24)      return `Uptime ${hours}h ${minutes % 60}m`;
  const days = Math.floor(hours / 24);
  return `Uptime ${days}d ${hours % 24}h`;
}

export async function fetchPlatformStatus(): Promise<PlatformStatusData> {
  const now = new Date().toISOString();

  const [apiResult, dbResult, incidentsResult] = await Promise.allSettled([
    fetch("/api/status", { signal: AbortSignal.timeout(8000) })
      .then((r) => r.json() as Promise<ApiStatusResponse>),
    supabase.from("profiles").select("id").limit(1),
    loadIncidentsFromSupabase(),
  ]);

  // Shape validation, not just fetch success: behind a proxy the endpoint can
  // answer 200 with a DIFFERENT JSON body (an error object, a platform page).
  // Trusting it blind crashed the status panel with "Cannot read properties
  // of undefined (reading 'ai')". A response without the expected fields is
  // treated exactly like an unreachable API.
  const raw = apiResult.status === "fulfilled" ? apiResult.value : null;
  const api: ApiStatusResponse | null =
    raw && typeof raw === "object" &&
    raw.services && typeof raw.services === "object" &&
    raw.ai && Array.isArray(raw.ai.providers)
      ? raw
      : null;
  const dbOk     = dbResult.status === "fulfilled" && !dbResult.value.error;
  const apiReach = api !== null;

  const services: ServiceHealth[] = [
    {
      name:        "AI Generation Services",
      status:      api ? toServiceStatus(api.services.ai) : "degraded",
      description: api
        ? `Active providers: ${api.ai.providers.join(", ") || "none configured"}`
        : "Unable to reach API",
      lastChecked: now,
    },
    {
      name:        "Build Infrastructure",
      status:      api ? toServiceStatus(api.services.redis) : "degraded",
      description: api
        ? `Queue / cache: ${api.services.redis}`
        : "Unable to reach API",
      lastChecked: now,
    },
    {
      name:        "API Gateway",
      status:      apiReach ? toServiceStatus(api!.services.api) : "outage",
      description: apiReach
        ? formatUptime(api!.uptime)
        : "API gateway unreachable",
      lastChecked: now,
    },
    {
      name:        "Database Services",
      status:      dbOk ? "operational" : "degraded",
      description: dbOk ? "Supabase responding normally" : "Database connectivity issue",
      lastChecked: now,
    },
    {
      name:        "Authentication Services",
      status:      dbOk ? "operational" : "degraded",
      description: dbOk ? "Auth services operational" : "Auth may be impacted",
      lastChecked: now,
    },
    {
      name:        "Preview Infrastructure",
      status:      apiReach ? "operational" : "degraded",
      description: apiReach ? "Static preview serving active" : "Preview serving unknown",
      lastChecked: now,
    },
  ];

  // Use Supabase incidents when available; fall back to current cache
  const loadedIncidents =
    incidentsResult.status === "fulfilled"
      ? incidentsResult.value
      : { active: currentStatus.activeIncidents, recent: currentStatus.recentIncidents };

  currentStatus = {
    overall:         "operational",
    message:         null,
    services,
    activeIncidents: loadedIncidents.active,
    recentIncidents: loadedIncidents.recent,
  };
  recalculateOverall();
  listeners.forEach((fn) => fn(currentStatus));
  return currentStatus;
}

/* =========================================================
   Public read API
   ========================================================= */

export function getPlatformStatus(): PlatformStatusData {
  return { ...currentStatus, services: currentStatus.services.map((s) => ({ ...s })) };
}

export function hasActiveIncident(): boolean {
  return currentStatus.activeIncidents.length > 0;
}

export function getIncidentBannerMessage(): string | null {
  if (currentStatus.activeIncidents.length === 0) return null;
  return currentStatus.activeIncidents[0].description;
}

export function onStatusUpdate(fn: (s: PlatformStatusData) => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/* =========================================================
   Incident write path
   reportIncident: optimistic in-memory update + fire-and-forget Supabase persist.
   resolveIncident: optimistic in-memory update + Supabase PATCH for UUID rows.
   The Supabase realtime subscription then reconciles all connected clients.
   ========================================================= */

export function reportIncident(
  incident: Omit<PlatformIncident, "id" | "createdAt" | "updatedAt">,
): string {
  const tempId = `inc-${Date.now()}`;
  const now    = new Date().toISOString();
  const rec: PlatformIncident = { ...incident, id: tempId, createdAt: now, updatedAt: now };

  currentStatus = { ...currentStatus, activeIncidents: [...currentStatus.activeIncidents, rec] };
  recalculateOverall();
  listeners.forEach((fn) => fn(currentStatus));

  // Persist to Supabase; realtime will replace the optimistic entry with the canonical UUID
  getBearerHeader().then((authHeader) => {
    if (!authHeader.Authorization) return;
    fetch("/api/incidents", {
      method:  "POST",
      headers: { ...authHeader, "Content-Type": "application/json" },
      body:    JSON.stringify({
        title:             incident.title,
        description:       incident.description,
        severity:          incident.severity,
        affected_services: incident.services,
      }),
    }).catch((err) => {
      console.warn("[platformStatus] reportIncident persist failed:", err?.message);
    });
  });

  return tempId;
}

export function resolveIncident(incidentId: string): void {
  const incident = currentStatus.activeIncidents.find((i) => i.id === incidentId);
  if (!incident) return;

  const resolved: PlatformIncident = {
    ...incident,
    status:     "resolved",
    resolvedAt: new Date().toISOString(),
    updatedAt:  new Date().toISOString(),
  };
  currentStatus = {
    ...currentStatus,
    activeIncidents: currentStatus.activeIncidents.filter((i) => i.id !== incidentId),
    recentIncidents: [resolved, ...currentStatus.recentIncidents].slice(0, 20),
  };
  recalculateOverall();
  listeners.forEach((fn) => fn(currentStatus));

  // Only Supabase UUID rows have a server record to update
  if (UUID_RE.test(incidentId)) {
    getBearerHeader().then((authHeader) => {
      if (!authHeader.Authorization) return;
      fetch(`/api/incidents/${incidentId}/resolve`, {
        method:  "PATCH",
        headers: authHeader,
      }).catch((err) => {
        console.warn("[platformStatus] resolveIncident persist failed:", err?.message);
      });
    });
  }
}

export function updateServiceStatus(serviceName: string, status: ServiceStatus): void {
  currentStatus = {
    ...currentStatus,
    services: currentStatus.services.map((s) =>
      s.name === serviceName
        ? { ...s, status, lastChecked: new Date().toISOString() }
        : s,
    ),
  };
  recalculateOverall();
  listeners.forEach((fn) => fn(currentStatus));
}

function recalculateOverall() {
  const hasOutage   = currentStatus.services.some((s) => s.status === "outage")
                   || currentStatus.activeIncidents.some((i) => i.severity === "critical");
  const hasDegraded = currentStatus.services.some((s) => s.status === "degraded")
                   || currentStatus.activeIncidents.length > 0;
  currentStatus.overall = hasOutage ? "outage" : hasDegraded ? "degraded" : "operational";
  currentStatus.message = hasOutage
    ? "Nuvra is experiencing temporary infrastructure issues."
    : hasDegraded
    ? "Some Nuvra services are experiencing degraded performance."
    : null;
}
